import { type FormEvent, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { shiftVacationsService } from '../../services/vacations/shift-vacations.service';
import type { ShiftVacation } from '../../models/vacations.types';
import { type WorkerSection, type ManagerSection } from './vacations-page.types';
import { messageFromError } from './vacations-page.utils';

export function useVacationsPageController() {
  const { user } = useShiftAuth();

  const isManager = user?.role === 'manager';

  const [vacations, setVacations] = useState<ShiftVacation[]>([]);
  const [section, setSection] = useState<WorkerSection | ManagerSection>(isManager ? 'all' : 'requested');
  const [startDay, setStartDay] = useState('');
  const [endDay, setEndDay] = useState('');
  const [comments, setComments] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const mutationLock = useRef(false);
  const pageLock = useRef(false);
  const loadGeneration = useRef(0);
  const nextCursor = useRef<string>();
  const nextOffset = useRef(0);
  const liveVacations = useRef(new Map<number, ShiftVacation | null>());

  const matchesSection = (vacation: ShiftVacation) => section === 'all' || (section === 'requested'
    ? ['pending', 'rejected', 'cancelled'].includes(vacation.status)
    : vacation.status === section);

  const mergeRows = (rows: ShiftVacation[]) => [...new Map(rows.map((row) => [row.id, row])).values()]
    .filter(matchesSection)
    .sort((left, right) => right.startDay.localeCompare(left.startDay) || right.id - left.id);

  const loadVacations = async (append = false) => {
    if (append && pageLock.current) return;
    pageLock.current = true;
    liveVacations.current.clear();
    const generation = ++loadGeneration.current;
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    try {
      const response = await shiftVacationsService.list({ offset: append ? nextOffset.current : 0, limit: 50, section,
        ...(append && nextCursor.current ? { cursor: nextCursor.current } : {}) });
      if (generation !== loadGeneration.current) return;
      nextOffset.current = (append ? nextOffset.current : 0) + response.vacations.length;
      nextCursor.current = response.pagination?.nextCursor;
      const liveRows = new Map(liveVacations.current);
      liveVacations.current.clear();
      setVacations((current) => {
        const merged = new Map([...(append ? current : []), ...response.vacations].map((row) => [row.id, row]));
        for (const [id, vacation] of liveRows) {
          if (vacation) merged.set(id, vacation);
          else merged.delete(id);
        }
        return mergeRows([...merged.values()]);
      });
      setHasMore(response.pagination?.hasMore ?? false);
    } catch (error) {
      if (generation !== loadGeneration.current) return;
      notifications.show({ color: 'red', message: messageFromError(error, 'טעינת החופשים נכשלה') });
    } finally {
      if (generation === loadGeneration.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
        pageLock.current = false;
      }
    }
  };

  const mergeVacation = (vacation: ShiftVacation) => {
    if (pageLock.current) liveVacations.current.set(vacation.id, vacation);
    setVacations((current) => mergeRows([...current, vacation]));
  };

  const eventsReady = useShiftEvents((event) => {
    if (event.type === 'vacation.upserted') mergeVacation(event.vacation);
    else if (event.type === 'vacation.removed') {
      if (pageLock.current) liveVacations.current.set(event.id, null);
      setVacations((current) => current.filter((row) => row.id !== event.id));
    }
    else if (event.type === 'sse.resync') void loadVacations();
  });

  useEffect(() => {
    if (eventsReady) void loadVacations();
    return () => { loadGeneration.current += 1; };
  }, [section, eventsReady]);

  const onCreateVacation = async (event: FormEvent) => {
    event.preventDefault();
    if (mutationLock.current) return;
    mutationLock.current = true;
    try {
      const created = await shiftVacationsService.create({ startDay, endDay, comments });
      mergeVacation(created);
      setStartDay('');
      setEndDay('');
      setComments('');
      notifications.show({ color: 'green', message: 'בקשת החופשה נשלחה' });
    } catch (error) {
      notifications.show({ color: 'red', message: messageFromError(error, 'שליחת הבקשה נכשלה') });
    } finally {
      mutationLock.current = false;
    }
  };

  const onDecideVacation = async (id: number, decision: 'approved' | 'rejected') => {
    if (mutationLock.current) return;
    mutationLock.current = true;
    try {
      const updated = await shiftVacationsService.decide(id, decision);
      mergeVacation(updated);
      notifications.show({
        color: 'green',
        message: decision === 'approved' ? 'הבקשה אושרה' : 'הבקשה נדחתה',
      });
    } catch (error) {
      notifications.show({ color: 'red', message: messageFromError(error, 'עדכון הבקשה נכשל') });
    } finally {
      mutationLock.current = false;
    }
  };

  const visible = vacations.filter((vacation) => {
    if (section === 'all') return true;
    if (section === 'requested') return ['pending', 'rejected', 'cancelled'].includes(vacation.status);
    return vacation.status === section;
  });

  const tabs = isManager
    ? ([
        ['all', 'כל הבקשות'],
        ['rejected', 'בקשות שנדחו'],
        ['approved', 'בקשות שאושרו'],
        ['occurred', 'בקשות שהתקיימו'],
      ] as const)
    : ([
        ['requested', 'התבקשו'],
        ['approved', 'אושרו'],
        ['occurred', 'התקיימו'],
      ] as const);

  return {
    isManager,
    section,
    setSection,
    startDay,
    setStartDay,
    endDay,
    setEndDay,
    comments,
    setComments,
    isLoading,
    hasMore,
    isLoadingMore,
    onLoadMore: () => loadVacations(true),
    onCreateVacation,
    onDecideVacation,
    visible,
    tabs,
  };
}
